// @flow
import * as React from 'react';
import { PropsWithChildren, useMemo } from 'react';
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { ComparablePlanType } from '@coverright/data-access/types/medicare';
import { Trash } from '@phosphor-icons/react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { usePlansForCompare } from '@coverright/features';
import { getNumbersFromString } from '@coverright/utils';
// ─── Types ────────────────────────────────────────────────────────────────────

interface AiDisplayPlan {
  bidId: string;
  name: string;
  premium: string;
  planType: ComparablePlanType;
}

interface AiPlanRowProps {
  plan: AiDisplayPlan;
}

interface PlanRowProps<T> {
  plan: T;
  getTitle: (plan: T) => string;
  getSubtitle?: (plan: T) => string;
  onDeleteClick: () => void;
}

type GroupProps = PropsWithChildren<{ type: ComparablePlanType; title: string }>

type PlanEntry<T> = T | AiDisplayPlan


type Props = {
  aiPlans?: AiDisplayPlan[];
  appContext: Parameters<typeof usePlansForCompare>[0];
  style?: React.CSSProperties;
  quoteId: string;
  clientId: string;
  slots?: {
    group?: React.FC<GroupProps>;
    plan?: React.FC<PlanRowProps<any>>;
    aiPlan?: React.FC<AiPlanRowProps>;
  };
  sx?: SxProps;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isAiPlan<T>(plan: PlanEntry<T>): plan is AiDisplayPlan {
  return !('__typename' in (plan as object));
}

function toSortValue(str: string | null | undefined): number {
  return getNumbersFromString(String(str ?? ''))[0] ?? Infinity;
}

function combinePlans<T>(
  realPlans: T[],
  getId: (plan: T) => string,
  planType: ComparablePlanType,
  getCost: (plan: T) => string | null | undefined,
  aiPlans: AiDisplayPlan[],
): PlanEntry<T>[] {
  const realIds = new Set(realPlans.map(getId));
  const filteredAi = aiPlans.filter(p => p.planType === planType && !realIds.has(p.bidId));
  return ([...realPlans, ...filteredAi] as PlanEntry<T>[]).sort((a, b) =>
    toSortValue(isAiPlan(a) ? a.premium : getCost(a)) -
    toSortValue(isAiPlan(b) ? b.premium : getCost(b))
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SelectedPlansList({ appContext, style, clientId, quoteId, slots, aiPlans = [], sx }: Props) {
  const { maPlans, pdpPlans, mgPlans, removePlan } = usePlansForCompare(appContext, quoteId, clientId);

  const GroupComponent = useMemo(() => slots?.group || Group, [slots?.group]);
  const PlanComponent: React.FC<PlanRowProps<any>> = useMemo(() => slots?.plan || PlanRow, [slots?.plan]);
  const AiPlanComponent = useMemo(() => slots?.aiPlan || DefaultAiPlanRow, [slots?.aiPlan]);

  const maCombined = useMemo(
    () => combinePlans(maPlans.plans, p => p.bidId, ComparablePlanType.Ma, p => p.monthlyCost, aiPlans),
    [aiPlans, maPlans.plans],
  );

  const mgCombined = useMemo(
    () => combinePlans(mgPlans.plans, p => p.key, ComparablePlanType.Mg, p => p.monthlyPremium, aiPlans),
    [aiPlans, mgPlans.plans],
  );

  const pdpCombined = useMemo(
    () => combinePlans(pdpPlans.plans, p => p.bidId, ComparablePlanType.Pdp, p => p.monthlyCost, aiPlans),
    [aiPlans, pdpPlans.plans],
  );

  return (
    <OverlayScrollbarsComponent
      style={style}
      options={{ paddingAbsolute: true }}
      defer
    >
      <Stack gap={4} sx={sx}>
        {(maCombined.length > 0 || maPlans.count > 0) && (
          <GroupComponent
            title={`Medicare Advantage (${maPlans.count})`}
            type={ComparablePlanType.Ma}
          >
            {maPlans.count > 0 && !maPlans.plans.length &&
              Array(maPlans.count)
                .fill(1)
                .map((_, i) => (
                  <Skeleton
                    variant={'rounded'}
                    key={i}
                    width={'100%'}
                    height={52}
                  />
                ))}
            {maCombined.map(entry =>
              isAiPlan(entry) ? (
                <AiPlanComponent key={entry.bidId} plan={entry} />
              ) : (
                <PlanComponent
                  key={entry.bidId}
                  plan={entry}
                  getTitle={plan => plan.planName || ''}
                  onDeleteClick={() => removePlan(ComparablePlanType.Ma, entry.bidId)}
                />
              )
            )}
          </GroupComponent>
        )}

        {(mgCombined.length > 0 || mgPlans.count > 0) && (
          <GroupComponent
            title={`Medicare Supplement (${mgPlans.count})`}
            type={ComparablePlanType.Mg}
          >
            {mgPlans.count > 0 && !mgPlans.plans.length &&
              Array(mgPlans.count)
                .fill(1)
                .map((_, i) => (
                  <Skeleton
                    variant={'rounded'}
                    key={i}
                    width={'100%'}
                    height={52}
                  />
                ))}
            {mgCombined.map(entry =>
              isAiPlan(entry) ? (
                <AiPlanComponent key={entry.bidId} plan={entry} />
              ) : (
                <PlanComponent
                  key={entry.key}
                  plan={entry}
                  getTitle={plan => 'Plan ' + plan.planName}
                  getSubtitle={plan => plan.parentOrgName || ''}
                  onDeleteClick={() => removePlan(ComparablePlanType.Mg, entry.key)}
                />
              )
            )}
          </GroupComponent>
        )}

        {(pdpCombined.length > 0 || pdpPlans.count > 0) && (
          <GroupComponent
            title={`Standalone Drug Plans (${pdpPlans.count})`}
            type={ComparablePlanType.Pdp}
          >
            {pdpPlans.count > 0 && !pdpPlans.plans.length &&
              Array(pdpPlans.count)
                .fill(1)
                .map((_, i) => (
                  <Skeleton
                    variant={'rounded'}
                    key={i}
                    width={'100%'}
                    height={52}
                  />
                ))}
            {pdpCombined.map(entry =>
              isAiPlan(entry) ? (
                <AiPlanComponent key={entry.bidId} plan={entry} />
              ) : (
                <PlanComponent
                  key={entry.bidId}
                  plan={entry}
                  getTitle={plan => plan.planName || ''}
                  onDeleteClick={() => removePlan(ComparablePlanType.Pdp, entry.bidId)}
                />
              )
            )}
          </GroupComponent>
        )}
      </Stack>
    </OverlayScrollbarsComponent>
  );
}

// ─── Default slot components ──────────────────────────────────────────────────

function DefaultAiPlanRow({ plan }: AiPlanRowProps) {
  return (
    <Box sx={{ p: '6px 12px', fontSize: 12, color: '#374151' }}>
      {plan.name} — {plan.premium}
    </Box>
  );
}

function PlanRow<T>({ plan, getTitle, getSubtitle, onDeleteClick }: PlanRowProps<T>) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
        p: '8px 12px',
        bgcolor: '#F3F4F6',
        borderRadius: '8px',
      }}
    >
      <Stack spacing={1} alignItems={'center'} direction={'row'}>
        <Typography color={'#4B5563'}>
          <b>{getTitle(plan)}</b>
        </Typography>
        {getSubtitle && <Typography color={'text.secondary'}>{getSubtitle(plan)}</Typography>}
      </Stack>
      <IconButton onClick={onDeleteClick}>
        <Trash size={20} color={'#1C434F'} />
      </IconButton>
    </Box>
  );
}

const Group = ({ type, title, children }: GroupProps) => {
  const color =
    type === ComparablePlanType.Ma
      ? '#F9E69A'
      : type === ComparablePlanType.Mg
      ? '#CBE4EC'
      : '#E1D9C7';

  return (
    <div>
      <Stack spacing={1} alignItems={'center'} direction={'row'} mb={2}>
        <Typography
          variant={'h4'}
          sx={{
            bgcolor: color,
            px: 0.8,
            fontSize: 12,
            letterSpacing: 0.5,
            lineHeight: '20px',
            borderRadius: '6px',
          }}
        >
          <b>{type}</b>
        </Typography>
        <Typography color={'#4B5563'}>
          <b>{title}</b>
        </Typography>
      </Stack>
      <Stack spacing={1}>{children}</Stack>
    </div>
  );
};
